import { useContext } from 'react';

import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import RolesContext from 'contexts/RolesContext';
import SelectedEntityContext from 'components/PerformanceBoards/contexts/SelectedEntityContext';
import PERMISSIONS from 'permissionDefinitions';

import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import { PERIOD_STATUS_LABELS } from '../../utils/getOverrideDisabledMessage';
import { isAdditionalPaymentPeriodLocked } from './utils';

/**
 * Whether the user may add / manage recognition payments for the slideout's
 * driver, shared by the Adjust menu and the Recognition Payments section.
 * Decided without waiting for the driver's bonus record: the program comes
 * from the program docs, and whether the driver already has payments from the
 * board row (`_additionalPayments`) until the record arrives.
 */
const useRecognitionPaymentAccess = () => {
  const { currentPermissions } = useContext(RolesContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const { bonusOverview, bonusProgramId, currentMode } =
    useContext(BonusOverviewContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { selectedEntityDocument } = useContext(SelectedEntityContext);
  const { isAdditionalPaymentsEnabled } = useContext(FeatureGatesContext);

  const bonusProgram =
    bonusOverview?.bonusProgram ??
    bonusPrograms.find((p) => p.id === bonusProgramId);
  const rowPayments = selectedEntityDocument?.['_additionalPayments'];
  const payments: unknown[] = bonusOverview
    ? bonusOverview.actualRewardOutput.additionalPayments ?? []
    : Array.isArray(rowPayments)
      ? rowPayments
      : [];

  const isLocked =
    !!selectedBonusPeriod &&
    isAdditionalPaymentPeriodLocked(selectedBonusPeriod.status);

  return {
    canAdd:
      isAdditionalPaymentsEnabled &&
      !!selectedBonusPeriod &&
      currentMode.mode === 'default' &&
      !!bonusProgram?.additionalPayments &&
      currentPermissions.includes(PERMISSIONS.DRIVER_BONUS.ADDITIONAL_PAYMENTS),
    hasPayments: payments.length > 0,
    isLocked,
    lockedMessage:
      isLocked && selectedBonusPeriod
        ? `Additional payments can't be changed once the period is "${
            PERIOD_STATUS_LABELS[selectedBonusPeriod.status]
          }".`
        : undefined,
  };
};

export default useRecognitionPaymentAccess;
